import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ImageUploadField, PageHeader, useToast } from 'components/admin';
import RichTextEditor from 'components/admin/RichTextEditor';
import { Button, Checkbox, Input, Select, Textarea } from 'components/common';
import { createIntroduceOverview, getAdminIntroduceMenus, getAdminIntroduceOverviewById, updateIntroduceOverview } from 'apis/adminIntroduce';
import {
    useActiveLanguagesQuery,
    useAdminCreateMutation,
    useAdminDetailQuery,
    useAdminListQuery,
    useAdminUpdateMutation,
} from 'hooks';
import '../../../styles/admin-shared.scss';

const IntroduceOverviewsForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const [activeTab, setActiveTab] = useState(0);
    const [formData, setFormData] = useState({ introduceMenuId: '', image: '', isActive: true, translation: [] });

    const { data: languages = [] } = useActiveLanguagesQuery();
    const { data: menuData = { items: [] } } = useAdminListQuery('introduce-menus-options', { pageSize: 1000, languageCode: 'vi' }, getAdminIntroduceMenus);
    const menus = menuData.items;
    const { data: detail, error: detailError } = useAdminDetailQuery('introduce-overviews', id, getAdminIntroduceOverviewById);
    const createMutation = useAdminCreateMutation('introduce-overviews', createIntroduceOverview);
    const updateMutation = useAdminUpdateMutation('introduce-overviews', updateIntroduceOverview);
    const loading = createMutation.isPending || updateMutation.isPending;

    useEffect(() => {
        if (!id && languages.length) setFormData(prev => ({ ...prev, translation: languages.map(l => ({ languageId: l.id, description: '', summary: '' })) }));
    }, [id, languages]);

    useEffect(() => {
        if (detail) setFormData({ ...detail, introduceMenuId: detail.introduceMenuId || '' });
    }, [detail]);

    useEffect(() => {
        if (detailError) toast.error(detailError.message || 'Tải dữ liệu thất bại');
    }, [detailError, toast]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleTranslationChange = (langId, field, value) => {
        setFormData(prev => ({ ...prev, translation: prev.translation.map(t => t.languageId === langId ? { ...t, [field]: value } : t) }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...formData, introduceMenuId: Number(formData.introduceMenuId) };
            const res = id
                ? await updateMutation.mutateAsync({ id, data: payload })
                : await createMutation.mutateAsync(payload);
            if (res.success) { toast.success(id ? 'Cập nhật thành công' : 'Thêm mới thành công'); navigate('/admin/introduce-overviews'); }
            else toast.error(res.message || 'Lưu thất bại');
        } catch { toast.error('Lưu thất bại'); }
    };

    return (
        <div className="admin-page">
            <PageHeader title={id ? 'Cập nhật Tổng quan Giới thiệu' : 'Thêm Tổng quan Giới thiệu'} />
            <form className="form-card" onSubmit={handleSubmit}>
                <p className="form-section-title">Cài đặt</p>
                <Select
                    label="Menu Giới thiệu *"
                    name="introduceMenuId"
                    value={formData.introduceMenuId}
                    onChange={handleChange}
                    options={[{ value: '', label: 'Chọn menu...' }, ...menus.map(m => {
                        const activeLangId = languages[activeTab]?.id;
                        const t = m.translations?.find(tr => tr.languageId === activeLangId) || m.translations?.[0];
                        return { value: m.id, label: t?.name || t?.title || m.name || `Menu ${m.id}` };
                    })]}
                    required
                />
                <ImageUploadField
                    label="Hình ảnh tổng quan"
                    value={formData.image}
                    folder="introduce/overviews"
                    onChange={(image) => setFormData(prev => ({ ...prev, image }))}
                />
                <Checkbox label="Hoạt động" name="isActive" checked={formData.isActive} onChange={handleChange} />
                {languages.length > 0 && (
                    <>
                        <p className="form-section-title">Bản dịch</p>
                        <div className="translation-tabs">
                            <div className="tab-header">{languages.map((lang, i) => <button key={lang.id} type="button" className={`tab-btn ${activeTab === i ? 'active' : ''}`} onClick={() => setActiveTab(i)}>{lang.name}</button>)}</div>
                            {languages.map((lang, i) => {
                                const t = formData.translation.find(t => t.languageId === lang.id) || {};
                                return activeTab === i ? (
                                    <div key={lang.id} className="tab-content">
                                        <div className="admin-form-group">
                                            <label>Mô tả *</label>
                                            <RichTextEditor
                                                value={t.description || ''}
                                                onChange={(content) => handleTranslationChange(lang.id, 'description', content)}
                                                placeholder={`Nhập mô tả (${lang.name})...`}
                                            />
                                        </div>
                                        <Textarea label="Tóm tắt" value={t.summary || ''} onChange={e => handleTranslationChange(lang.id, 'summary', e.target.value)} rows={3} />
                                    </div>
                                ) : null;
                            })}
                        </div>
                    </>
                )}
                <div className="form-actions">
                    <Button type="submit" disabled={loading}>{loading ? 'Đang lưu...' : id ? 'Cập nhật' : 'Thêm mới'}</Button>
                    <Button type="button" variant="secondary" onClick={() => navigate('/admin/introduce-overviews')}>Hủy</Button>
                </div>
            </form>
        </div>
    );
};

export default IntroduceOverviewsForm;
